import { useEffect, useRef } from "react";
import type { ApiClient, WorldViewActivity } from "./api";
import { apiProblem, useLoaded } from "./useLoaded";

export interface WorldViewActivityState {
  activity: WorldViewActivity | null;
  problem: string | null;
  /** Start polling right away — an approve answer carrying `synthesizing: true` calls
   * this instead of waiting for the next natural poll. */
  reload: () => void;
}

const readActivity = (api: ApiClient): Promise<WorldViewActivity | null> => api.getWorldViewActivity();
const pollWhileRunning = (activity: WorldViewActivity | null) => (activity?.running != null ? 2000 : null);
const problem = apiProblem("The world view's activity could not be read.");

/**
 * UI-23 — the World view's own activity, polled every 2s only while a pass is running.
 *
 * `onIdle` fires on the running-to-idle transition, not on an already-idle initial load —
 * it is what lets a subject page open during a batch fill in the moment the pass ends,
 * rather than only on the next visit.
 */
export function useWorldViewActivity(api: ApiClient, onIdle?: () => void): WorldViewActivityState {
  const { value, problem: failure, refresh } = useLoaded(api, readActivity, null, problem, {
    poll: pollWhileRunning,
  });

  const running = value?.running != null;
  const wasRunning = useRef(false);
  const idle = useRef(onIdle);
  idle.current = onIdle;

  useEffect(() => {
    if (wasRunning.current && !running) idle.current?.();
    wasRunning.current = running;
  }, [running]);

  return { activity: value, problem: failure, reload: refresh };
}
